import { GetAdminOnboardingResponse } from '@orbit/api-types'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

interface UseAdminOnboardingParams {
	page: number
	pageSize?: number
	/** Search the answer rows by name or email; the aggregates ignore it. */
	q?: string
}

/**
 * Fetches the admin onboarding survey view (GET /v1/admin/onboarding), scoped to
 * the global creation-date range from {@link useAdminFilter}. The range defines
 * the signup cohort every aggregate describes; `page` and `q` only move the
 * per-user rows. All of them are part of the query key, so changing any refetches,
 * and previous data is kept while the next page loads. Gated server-side by the
 * AdminGuard; non-admins receive 403.
 */
export const useAdminOnboarding = ({ page, pageSize, q }: UseAdminOnboardingParams) => {
	const { from, to, includeInternal } = useAdminFilter()

	return useQuery<GetAdminOnboardingResponse>({
		queryKey: queryKeys.adminOnboarding(from, to, page, pageSize, q, includeInternal),
		queryFn: async () => {
			const response = await api.get<GetAdminOnboardingResponse>('/admin/onboarding', {
				params: {
					from,
					to,
					page,
					pageSize,
					q: q || undefined,
					includeInternal: includeInternal || undefined,
				},
			})
			return response.data
		},
		placeholderData: keepPreviousData,
	})
}
